import { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import { GlobalContext } from "../../context/globalContext";
import { httpAPI_APP016, httpAPI_RET017 } from "./api/request";
import { fakeTableList } from "./parts";
import "./_style.scss";
import { Box } from "@mui/material";
import {
  applyEvent,
  Prefectures,
  Relationship,
  Status,
} from "../../assets/data/Constants";
import TkMemo from "../../components/molecules/TkMemo";
import TkViewButtonJudgement from "../../components/molecules/TkViewButtonJudgement";
import { ITkViewButtonJudgementStatus } from "../../type/componentsType/TkViewButtonJudgement";
import TkKikoStepper from "../../components/molecules/TkKikoStepper";
import { BusinessContext } from "../../context/businessContext";
import { changeZipcodeFormat } from "../../utils/useTools";
import {
  DownloadLinkWithCheck,
  RelationshipInput,
} from "./components/components";
import { TkGetCode } from "../../utils/TkGetCode";
import { ICommonProps } from "../../type/contextType/businessContext/ICommon";
import { FormList, MissingFileList } from "../../type/apiType/IAPI_RET017";
import _ from "lodash";

/**
 * SCRW410004 退職手当金受領者の変更手続き（機構へ連絡）
 * @return SCRW410004
 */
const SCRW410004 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState } = useContext(BusinessContext);

  const [stateTableList, setStateTableList] = useState<any>([]);
  const stateFakeTableListRef = useRef(_.cloneDeep(fakeTableList));

  const [bodyList, setBodyList] = useState<any>([]);
  const [statusCd, setStatusCd] = useState<string>();
  const [corpApplyKbn, setCorpApplyKbn] = useState<"1" | "other" | undefined>();
  const [memo, setMemo] = useState<string>("");
  const [inquiryCode, setInquiryCode] = useState<string>("");
  const [missingFileList, setMissingFileList] = useState<MissingFileList[]>([]);
  const applyId = location.state.applyId ?? businessState.job_40?.applyId;

  // 初期表示
  const handleInit = async () => {
    // 受領者変更参照API 呼び出し
    try {
      const res = await httpAPI_RET017({
        applyId,
        contractorNo: businessState.common?.contractorNo,
      });

      if (res.resultCd === "I0000") {
        let resArr = stateFakeTableListRef.current;

        for (let i in res) {
          let targetObj = resArr.find((item) => item.keyword === i)!;

          if (targetObj) {
            targetObj.text = res[i as keyof typeof res];
            targetObj.setDefaultValue = res[i as keyof typeof res];
          }
        }

        resArr.find((item) => item.keyword === "demanderRelationship")!.node = (
          <RelationshipInput
            defaultValue={res.demanderRelationship}
            other={res.demanderRelationshipOthers}
          />
        );

        // 請求者現住所（郵便番号）
        resArr.find((item) => item.keyword === "demanderZipcode")!.text =
          changeZipcodeFormat(res?.demanderZipcode);

        // 請求者現住所（郵便番号以外）
        let address = resArr.find((item) => item.keyword === "address")!;
        address.text =
          (TkGetCode(Prefectures, res.demanderPrefectureCd) ?? "") +
          (res?.demanderCity ?? "") +
          (res?.demanderAddressOthers ?? "");

        let commonLawRelationship =
          res.demanderRelationship === Relationship["内縁の妻又は夫"];
        // ④内縁関係についての「生計維持に関する調査書」（様式１１号）
        resArr.find(
          (item) => item.keyword === "commonLawRelationship"
        )!.showLine = commonLawRelationship;
        // ⑤職員の死亡当時の世帯員の状況を明らかにできる「住民票の謄本」
        resArr.find(
          (item) => item.keyword === "certificateOfResidence"
        )!.showLine = commonLawRelationship;
        // ⑥その他、事実上の配偶者であることを証明する書類
        resArr.find((item) => item.keyword === "lawfulPartner")!.showLine =
          commonLawRelationship;

        // ②職員の死亡当時、主として職員の収入により生計を維持していた親族がいることを証する「生計維持に関する調査書」（様式１１号）
        resArr.find(
          (item) => item.keyword === "livelihoodSupportRelatives"
        )!.showLine =
          !commonLawRelationship && res.livelihoodFromRetireeFlg === "1";
        // ②職員の死亡当時、主として職員の収入により生計を維持していた親族がいないことを証する「生計維持に関する調査書」（様式１１号）
        resArr.find(
          (item) => item.keyword === "livelihoodNoSupportRelatives"
        )!.showLine =
          !commonLawRelationship && res.livelihoodFromRetireeFlg === "0";

        resArr.forEach((item) => {
          if (item.keyword === "demanderRelationship") return;

          let form = res.fileList?.find((i) => i.formClass === item.id) ?? {};
          let missingFile = res.missingFileList?.find(
            (i) => i.missingFormClass === item.id
          );

          item.node = (
            <DownloadLinkWithCheck
              fileName={form?.formName ?? ""}
              base64={form?.file ?? ""}
              defaultChecked={!!missingFile}
              disabled={
                !(
                  res.latestApplyStatus === Status["受理待ち"] ||
                  res.latestApplyStatus === Status["再受理待ち"]
                )
              }
              onClick={(event: any) => handleResubmitClick(event, form)}
            />
          );
        });

        setStateTableList([...resArr]);

        // 処理状況
        const infoBodyList = res.memoList?.map((item) => [
          {
            children: item.managerName,
          },
          {
            children: item.process,
          },
          {
            children: item.createDate,
            align: "center",
          },
          {
            children: item.processComment,
          },
        ]);

        setBodyList(infoBodyList);

        setStatusCd(res.latestApplyStatus!);
        setCorpApplyKbn(res.corpApplyKbn as keyof typeof corpApplyKbn);
        setMissingFileList(res.missingFileList ?? []);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  // 初期表示
  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResubmitClick = (clicked: any, value: FormList) => {
    let form: MissingFileList = {
      missingFormKbn: value.formKbn,
      missingFormClass: value.formClass,
      missingFormName: value.formName,
    };

    setMissingFileList((prev: any) => {
      if (clicked) {
        return [...prev, form];
      } else {
        return prev.filter((i: any) => i.missingFormClass !== value.formClass);
      }
    });
  };

  const onChange = (event: any) => {
    if (event.target.type === "input") {
      setMemo(event.target.value);
    } else {
      setInquiryCode(event.target.value ? "3" : "");
    }
  };

  // 処理ボタン押下
  const onCheckAndSubmit = async (btnName: keyof typeof applyEvent) => {
    const event = applyEvent[btnName];

    switch (event) {
      case applyEvent["受理"]:
      case applyEvent["契約者に差戻し"]:
      case applyEvent["担当者に差戻し"]:
      case applyEvent["課長代理確認"]:
      case applyEvent["上長承認"]:
        // 申請状況更新API 呼び出し
        try {
          let newFileList = missingFileList;
          newFileList.forEach((item, index) => {
            item.missingSerialNo = index + 1;
          });

          const res = await httpAPI_APP016({
            applyId,
            applyEvent: event,
            screenId: "SCRW410004",
            contractorNo: businessState.common?.contractorNo,
            reason: memo,
            missingFileList: event === applyEvent["受理"] ? newFileList : [],
            inquiryCode: inquiryCode,
          });

          if (res.resultCd === "I0000") {
            setState({
              ...state,
              errorMsgId: "",
            });
            navigate("/SCRW110001");
          } else {
            setState({
              ...state,
              errorMsgId: res.resultCd,
            });
          }
        } catch (error) {
          navigate("/error");
        }
        break;

      case applyEvent["通知"]:
        navigate("/SCRW130001", {
          state: {
            applyId,
            contractorNo: businessState.common?.contractorNo,
            screenId: "SCRW410004",
            memo,
            inquiryCode: inquiryCode,
          } as ICommonProps,
        });
        break;

      default:
        return;
    }
  };

  return (
    <Box className="SCRW410004" data-testid="SCRW410004PageForTest">
      <TkKikoStepper
        applicationFlowType={"TYPE3"}
        businessType={"JOB-40"}
        applicationStatus={statusCd as ITkViewButtonJudgementStatus}
        corporateApplicationClassification={corpApplyKbn}
      />

      <TkFakeTable list={stateTableList} ruleSchema={{}} showButton={false} />

      <Box className="memo" data-testid="tkTextField">
        <TkMemo onChange={onChange} bodyList={bodyList} />
      </Box>

      <Box className="btn">
        <TkBackButton className="tkBottonMr" />
        <TkViewButtonJudgement
          flowType={"TYPE3"}
          Status={statusCd as ITkViewButtonJudgementStatus}
          onClick={onCheckAndSubmit}
        />
      </Box>
    </Box>
  );
};

export default SCRW410004;
